import _ from 'lodash';
import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  elementVisibilityChanged,
  startFreeTrialInit,
  startFreeTrialDestroy,
} from './FloatingStartFreeTrial.actions';

function getObserver(
  dispatchElementVisibilityChange,
  { rootMargin = '0px', threshold = 0.2 } = {},
) {
  const options = {
    root: null,
    rootMargin,
    threshold: [threshold],
  };
  const observer = new window.IntersectionObserver(dispatchElementVisibilityChange, options);

  return observer;
}

function useInterSectionObserver(ref, { onInterSection, onClear, rootMargin, threshold }) {
  useEffect(() => {
    const element = ref.current;
    if (element) {
      const observer = getObserver(onInterSection, { rootMargin, threshold });
      observer.observe(element);
      return () => {
        observer.unobserve(element);
        onClear({ element });
      };
    }
    return _.noop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/**
 *
 * useStartFreeTrialState is used for controlling the visible and compress state.
 * any action can be dispatched based on the element interaction passed to this
 * hook.
 */
export function useStartFreeTrialState(
  ref,
  { actionToDispatch = elementVisibilityChanged, rootMargin = '0px', threshold = 0.2 } = {},
) {
  const dispatch = useDispatch();
  const onInterSection = useCallback((...args) => {
    const [[intersectionObserverEntry]] = args;
    dispatch(
      actionToDispatch({
        target: intersectionObserverEntry.target,
        isIntersecting: intersectionObserverEntry.isIntersecting,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClear = useCallback(({ element }) => {
    dispatch(actionToDispatch({ target: element, isIntersecting: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useInterSectionObserver(ref, { onInterSection, onClear, rootMargin, threshold });
}

export function useStartFreeTrialMounting() {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(startFreeTrialInit());
    return () => {
      dispatch(startFreeTrialDestroy());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
